/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';

import template from './creative-context-actions.component.html';
import {wireModelCommsModelActions} from '../common/controller/model-comms-actions';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {CreativeCopyModalService} from './creative-copy-modal.service';
import {CreativeLinkLibraryModalService} from './creative-link-library-modal.service';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {bufferTemplateFlag as bufferSharedTemplateFlag, BufferedFlag} from '../common/util/template-state';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-creative-context-actions',
  template
})
export class CreativeContextActionsComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() blockDelete: any;

  /**
   * Route isNew never flips after save; modelComms does. Bind templates to this
   * so a sync subscriber callback cannot mutate the checked [isNew] mid-CD (NG0100).
   */
  effectiveIsNew = false;

  specifiedAdvertiser: any;

  private readonly isNewFlag = {display: false, last: undefined as boolean | undefined};
  private readonly readOnlyFlag = {display: false, last: undefined as boolean | undefined};
  private readonly blockDeleteFlag = {display: false, last: undefined as boolean | undefined};

  private subscribedModelComms: any;
  private destroyed = false;

  constructor(
    private readonly $state: StateService,
    private readonly toastr: ToastrService,
    @Inject(CreativeResource) private readonly Creative: any,
    private readonly copyModalService: CreativeCopyModalService,
    private readonly linkLibraryModalService: CreativeLinkLibraryModalService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  /**
   * Buffered for [isNew] — keeps the property binding stable if effectiveIsNew
   * flips during the same CD turn (NG0100 true→false).
   */
  get displayIsNew(): boolean {
    return this.bufferTemplateFlag(this.isNewFlag, !!this.effectiveIsNew);
  }

  get displayReadOnly(): boolean {
    return this.bufferTemplateFlag(this.readOnlyFlag, !!this.readOnly);
  }

  get displayBlockDelete(): boolean {
    return this.bufferTemplateFlag(this.blockDeleteFlag, !!this.blockDelete);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.setEffectiveIsNew(!!this.isNew);
    }
    if (this.modelComms) {
      wireModelCommsModelActions(this.modelComms, this.Creative);
    }
    this.ensureModelCommsSubscription();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  linkLibrary(): void {
    if (this.readOnly || this.blockDelete || !this.model) {
      return;
    }
    this.linkLibraryModalService.open({
      model: this.model,
      modelComms: this.modelComms
    });
  }

  copy(): void {
    if (this.readOnly || this.blockDelete || !this.model) {
      return;
    }
    this.copyModalService.open({
      model: this.model,
      specifiedAdvertiser: this.specifiedAdvertiser,
      onComplete: (result) => {
        if (result.destination && result.creativeId) {
          this.$state.go('app.adv.creatives.creative', {id: result.creativeId});
        } else if (!result.destination) {
          this.toastr.success('A copy of this creative was successfully created.', 'Creative Copied');
        }
      }
    });
  }

  private setEffectiveIsNew(value: boolean): void {
    if (this.effectiveIsNew === value) {
      return;
    }
    this.effectiveIsNew = value;
  }

  private bufferTemplateFlag(
    state: BufferedFlag,
    next: boolean
  ): boolean {
    return bufferSharedTemplateFlag(state, next, {
      isAlive: () => !this.destroyed,
      onCommit: () => this.cdr.markForCheck()
    });
  }

  private ensureModelCommsSubscription(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }

    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNew: boolean) => {
      if (this.destroyed) {
        return;
      }
      // addSubscriber / modelUpdated can run synchronously during CD (including
      // immediate replay). Defer so [isNew] does not flip true→false after it
      // was checked (NG0100).
      deferMarkForCheck(this.cdr, () => {
        if (this.destroyed) {
          return;
        }
        if (typeof isNew === 'boolean') {
          this.setEffectiveIsNew(isNew);
        }
        if (_.get(type, ['name']) === OBJECT_TYPE.Advertiser.name) {
          this.specifiedAdvertiser = data;
        }
        if (!type && this.model && data) {
          this.model.libraryCreative = data.libraryCreative;
        }
      });
    });
  }
}
